import { ElementViewTemplate, html, ref, slotted } from "@microsoft/fast-element";
import { NatGenListbox } from "../listbox/listbox";
import { endSlotTemplate, startSlotTemplate } from "../start-end/start-end";
import { staticallyCompose } from "../../foundation/template-helpers";
import { ComboboxOptions } from "./combobox.options";
import { NatGenCombobox } from "./combobox";

export function comboboxTemplate<T extends NatGenCombobox>(
    options: ComboboxOptions = {}
): ElementViewTemplate<T> {
    return html<T>`
        <template
            aria-disabled="${x => x.ariaDisabled}"
            autocomplete="${x => x.autocomplete}"
            ?open="${x => x.open}"
            tabindex="${x => (!x.disabled ? "0" : null)}"
            @click="${(x, c) => x.clickHandler(c.event as MouseEvent)}"
            @focusin="${(x, c) => x.focusinHandler(c.event as FocusEvent)}"
            @focusout="${(x, c) => x.focusoutHandler(c.event as FocusEvent)}"
            @keydown="${(x, c) => x.keydownHandler(c.event as KeyboardEvent)}"
        >
            <div class="control" part="control">
                ${startSlotTemplate(options)}
                <natgen-text-field
                    appearance="stealth"
                    aria-activedescendant="${x =>
                        x.open ? x.ariaActiveDescendant : null}"
                    aria-autocomplete="${x => x.ariaAutoComplete}"
                    aria-controls="${x => x.ariaControls}"
                    aria-disabled="${x => x.ariaDisabled}"
                    aria-expanded="${x => x.ariaExpanded}"
                    aria-invalid="${x => x.ariaInvalid}"
                    aria-haspopup="listbox"
                    aria-label="${x => x.ariaLabel}"
                    aria-labelledby="${x => x.ariaLabelledby}"
                    class="selected-value"
                    part="selected-value"
                    placeholder="${x => x.placeholder}"
                    role="combobox"
                    type="text"
                    ?disabled="${x => x.disabled}"
                    current-value="${x => x.displayValue}"
                    @input="${(x, c) => x.inputHandler(c.event as InputEvent)}"
                    @keyup="${(x, c) => x.keyupHandler(c.event as KeyboardEvent)}"
                    ${ref("control")}>
                    <slot name="label"></slot>
                </natgen-text-field>
                <div class="indicator" part="indicator" aria-hidden="true">
                    <slot name="indicator">
                        ${staticallyCompose(options.indicator)}
                    </slot>
                </div>
                ${endSlotTemplate(options)}
            </div>
            <div
                class="listbox"
                id="${x => x.listboxId}"
                part="listbox"
                role="listbox"
                ?disabled="${x => x.disabled}"
                ?hidden="${x => (x.collapsible ? !x.open : false)}"
                ${ref("listbox")}
            >
                <slot
                    ${slotted({
                        filter: NatGenListbox.slottedOptionFilter,
                        flatten: true,
                        property: "slottedOptions",
                    })}
                ></slot>
            </div>
        </template>
    `;
}
